import { useState, useRef } from 'react';
import { api } from '../../context/AuthContext';
import { HomeFeatureStar } from './HomeFeatureStar';
import { API, API_BASE } from '../../config/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Loader2, Music, Upload, Download, Play, Disc, X, GripVertical, ArrowUp, ArrowDown } from 'lucide-react';
import { PreviewModeToggle } from './PreviewModeToggle';


export function FreeMediaTab({ freeMedia, onRefresh }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [albumDialogOpen, setAlbumDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingBackCover, setUploadingBackCover] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [form, setForm] = useState({
    title: '', artist: '', description: '', cover_image: '', back_cover: '', audio_file: '', is_downloadable: false, album_name: '', release_date: '', spotify_url: ''
  });

  // Album bulk upload state
  const [albumForm, setAlbumForm] = useState({
    album_name: '', artist: '', cover_image: '', is_downloadable: false
  });
  const [albumTracks, setAlbumTracks] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [savingAlbum, setSavingAlbum] = useState(false);
  const [albumCoverUploading, setAlbumCoverUploading] = useState(false);
  const [addingToAlbum, setAddingToAlbum] = useState(null); // album name when adding tracks to existing album
  const fileInputRef = useRef(null);
  const addTracksFileRef = useRef(null);

  const resetForm = () => {
    setForm({ title: '', artist: '', description: '', cover_image: '', back_cover: '', audio_file: '', is_downloadable: false, album_name: '', release_date: '', spotify_url: '' });
    setEditing(null);
  };

  const resetAlbumForm = () => {
    setAlbumForm({ album_name: '', artist: '', cover_image: '', is_downloadable: false });
    setAlbumTracks([]);
    setAddingToAlbum(null);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      title: item.title || '', artist: item.artist || '', description: item.description || '',
      cover_image: item.cover_image || '', back_cover: item.back_cover || '', audio_file: item.audio_file || '',
      is_downloadable: item.is_downloadable || false, album_name: item.album_name || '',
      release_date: item.release_date || '', spotify_url: item.spotify_url || ''
    });
    setDialogOpen(true);
  };

  const handleAudioUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post(`${API}/upload/audio`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(prev => ({ ...prev, audio_file: data.path }));
      toast.success('Audio uploaded!');
    } catch { toast.error('Failed to upload audio'); }
    setUploading(false);
  };

  const handleCoverUpload = async (e, isAlbum = false) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (isAlbum) setAlbumCoverUploading(true);
    else setUploadingCover(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post(`${API}/upload/cover`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const imageUrl = `${API_BASE}${data.url}`;
      if (isAlbum) setAlbumForm(prev => ({ ...prev, cover_image: imageUrl }));
      else setForm(prev => ({ ...prev, cover_image: imageUrl }));
      toast.success('Cover uploaded!');
    } catch { toast.error('Failed to upload cover'); }
    if (isAlbum) setAlbumCoverUploading(false);
    else setUploadingCover(false);
  };

  const handleBackCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingBackCover(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post(`${API}/upload/cover`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const imageUrl = `${API_BASE}${data.url}`;
      setForm(prev => ({ ...prev, back_cover: imageUrl }));
      toast.success('Back cover uploaded!');
    } catch { toast.error('Failed to upload back cover'); }
    setUploadingBackCover(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`${API}/free-media/${editing.id}`, form);
        toast.success('Updated!');
      } else {
        await api.post(`${API}/free-media`, form);
        toast.success('Created!');
      }
      setDialogOpen(false);
      resetForm();
      onRefresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      let msg = 'Failed to save';
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail)) msg = detail.map(d => typeof d === 'string' ? d : (d?.msg || JSON.stringify(d))).join(', ');
      else if (detail && typeof detail === 'object') msg = detail.msg || JSON.stringify(detail);
      toast.error(msg);
    }
  };

  // Bulk audio upload for album
  const handleBulkAudioUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBulkUploading(true);

    const newTracks = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await api.post(`${API}/upload/audio`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const name = file.name.replace(/\.[^/.]+$/, '').replace(/^\d+[\s._-]*/, '');
        newTracks.push({ title: name, audio_file: data.path, fileName: file.name });
      } catch {
        toast.error(`Failed to upload: ${file.name}`);
      }
    }
    setAlbumTracks(prev => [...prev, ...newTracks]);
    setBulkUploading(false);
    if (newTracks.length > 0) toast.success(`${newTracks.length} track${newTracks.length > 1 ? 's' : ''} uploaded!`);
  };

  const removeAlbumTrack = (index) => {
    setAlbumTracks(prev => prev.filter((_, i) => i !== index));
  };

  const updateAlbumTrackTitle = (index, title) => {
    setAlbumTracks(prev => prev.map((t, i) => i === index ? { ...t, title } : t));
  };

  const handleAlbumSubmit = async () => {
    if (!albumForm.album_name.trim()) { toast.error('Album name is required'); return; }
    if (albumTracks.length === 0) { toast.error('Upload at least one track'); return; }
    setSavingAlbum(true);
    try {
      const { data } = await api.post(`${API}/free-media/bulk`, {
        album_name: albumForm.album_name,
        artist: albumForm.artist,
        cover_image: albumForm.cover_image,
        is_downloadable: albumForm.is_downloadable,
        tracks: albumTracks.map(t => ({ title: t.title, audio_file: t.audio_file }))
      });
      toast.success(data.message);
      setAlbumDialogOpen(false);
      resetAlbumForm();
      onRefresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Failed to save album';
      toast.error(msg);
    }
    setSavingAlbum(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await api.delete(`${API}/free-media/${id}`);
      toast.success('Deleted!');
      onRefresh();
    } catch { toast.error('Failed to delete'); }
  };

  const handleDeleteAlbum = async (albumName) => {
    const items = freeMedia.filter(i => i.album_name === albumName);
    if (!window.confirm(`Delete all ${items.length} tracks in "${albumName}"?`)) return;
    try {
      await Promise.all(items.map(i => api.delete(`${API}/free-media/${i.id}`)));
      toast.success(`Album "${albumName}" deleted!`);
      onRefresh();
    } catch { toast.error('Failed to delete album'); }
  };

  // Add tracks to an existing album
  const handleAddTracksToAlbum = (albumName) => {
    const albumItems = freeMedia.filter(i => i.album_name === albumName);
    setAlbumForm({
      album_name: albumName,
      artist: albumItems[0]?.artist || '',
      cover_image: albumItems[0]?.cover_image || '',
      is_downloadable: albumItems[0]?.is_downloadable || false
    });
    setAlbumTracks([]);
    setAddingToAlbum(albumName);
    setAlbumDialogOpen(true);
  };

  const handleAddTracksFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBulkUploading(true);
    const newTracks = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await api.post(`${API}/upload/audio`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const name = file.name.replace(/\.[^/.]+$/, '').replace(/^\d+[\s._-]*/, '');
        newTracks.push({ title: name, audio_file: data.path, fileName: file.name });
      } catch {
        toast.error(`Failed to upload: ${file.name}`);
      }
    }
    setAlbumTracks(prev => [...prev, ...newTracks]);
    setBulkUploading(false);
    if (newTracks.length > 0) toast.success(`${newTracks.length} track${newTracks.length > 1 ? 's' : ''} uploaded!`);
  };

  const moveTrack = async (trackList, index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= trackList.length) return;
    setReordering(true);
    const reordered = [...trackList];
    [reordered[index], reordered[newIndex]] = [reordered[newIndex], reordered[index]];
    try {
      await api.put(`${API}/free-media/reorder`, { ordered_ids: reordered.map(t => t.id) });
      onRefresh();
    } catch { toast.error('Failed to reorder'); }
    setReordering(false);
  };

  const moveAlbum = async (albumNames, index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= albumNames.length) return;
    setReordering(true);
    const reordered = [...albumNames];
    [reordered[index], reordered[newIndex]] = [reordered[newIndex], reordered[index]];
    try {
      await api.put(`${API}/free-media/reorder-albums`, { album_names: reordered });
      toast.success('Albums reordered!');
      onRefresh();
    } catch { toast.error('Failed to reorder albums'); }
    setReordering(false);
  };

  const toggleDownloadable = async (item) => {
    try {
      await api.put(`${API}/free-media/${item.id}`, { is_downloadable: !item.is_downloadable });
      onRefresh();
    } catch { toast.error('Failed to update'); }
  };

  const handleAlbumCoverChange = async (e, tracks) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post(`${API}/upload/cover`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const imageUrl = `${API_BASE}${data.url}`;
      await Promise.all(tracks.map(t => api.put(`${API}/free-media/${t.id}`, { cover_image: imageUrl })));
      toast.success('Album cover updated!');
      onRefresh();
    } catch { toast.error('Failed to update cover'); }
  };

  // Group items by album
  const albums = {};
  const singles = [];
  freeMedia.forEach(item => {
    if (item.album_name) {
      if (!albums[item.album_name]) albums[item.album_name] = [];
      albums[item.album_name].push(item);
    } else {
      singles.push(item);
    }
  });
  // Sort album tracks by track_number
  Object.keys(albums).forEach(k => albums[k].sort((a, b) => (a.track_number || 0) - (b.track_number || 0)));
  // Sort album names by release_date (year), then album_order
  const albumNames = Object.keys(albums).sort((a, b) => {
    const yearA = albums[a][0]?.release_date || '9999';
    const yearB = albums[b][0]?.release_date || '9999';
    if (yearA !== yearB) return yearA.localeCompare(yearB);
    const orderA = albums[a][0]?.album_order ?? 999;
    const orderB = albums[b][0]?.album_order ?? 999;
    return orderA - orderB;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-cabinet text-2xl font-black uppercase">Free Media</h2>
          <p className="text-[#A3A3A3] text-sm mt-1">Upload offline music for free streaming & downloads</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="border-[#FF3B30] bg-[#FF3B30]/10 text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white rounded-none font-black"
            onClick={async () => {
              if (!window.confirm('Set ALL free-media items to 30s preview mode (and fill missing unlock prices with $1.99)?\n\nYou can still flip individual items back to FULL afterwards.')) return;
              try {
                const { data } = await api.post(`${API}/free-media/bulk-set-preview-mode`,
                  { mode: 'snippet', unlock_price: 1.99 }, { withCredentials: true });
                toast.success(`Set ${data.modified} items to 30s preview${data.price_filled ? ` · filled $1.99 on ${data.price_filled}` : ''}`);
                if (onRefresh) onRefresh();
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Bulk update failed');
              }
            }}
            data-testid="bulk-snippet-btn"
          >
            SET ALL TO 30s PREVIEW
          </Button>
          <Button
            variant="outline"
            className="border-[#22C55E] bg-[#22C55E]/10 text-[#22C55E] hover:bg-[#22C55E] hover:text-black rounded-none font-black"
            onClick={async () => {
              if (!window.confirm('Set ALL free-media items back to FULL playback (removes 30s preview cap)?')) return;
              try {
                const { data } = await api.post(`${API}/free-media/bulk-set-preview-mode`,
                  { mode: 'full' }, { withCredentials: true });
                toast.success(`Set ${data.modified} items to FULL playback`);
                if (onRefresh) onRefresh();
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Bulk update failed');
              }
            }}
            data-testid="bulk-full-btn"
          >
            SET ALL TO FULL
          </Button>
          <Dialog open={albumDialogOpen} onOpenChange={(open) => { setAlbumDialogOpen(open); if (!open) resetAlbumForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#DAA520] text-black hover:bg-white rounded-none" data-testid="upload-album-btn">
                <Disc className="w-4 h-4 mr-2" /> Upload Album
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#141414] border-[#262626] rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-cabinet font-bold uppercase">{addingToAlbum ? `Add Tracks to "${addingToAlbum}"` : 'Upload Album'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {!addingToAlbum && (
                <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Album Name *</Label>
                    <Input value={albumForm.album_name} onChange={(e) => setAlbumForm(prev => ({ ...prev, album_name: e.target.value }))}
                      required className="bg-[#0A0A0A] border-[#262626] rounded-none" placeholder="My Album" data-testid="album-name-input" />
                  </div>
                  <div>
                    <Label>Artist</Label>
                    <Input value={albumForm.artist} onChange={(e) => setAlbumForm(prev => ({ ...prev, artist: e.target.value }))}
                      className="bg-[#0A0A0A] border-[#262626] rounded-none" data-testid="album-artist-input" />
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <Label>Album Cover</Label>
                    <div className="flex items-center gap-3 mt-1">
                      {albumForm.cover_image && <img src={albumForm.cover_image} alt="" className="w-16 h-16 object-cover border border-[#262626]" />}
                      <label className="cursor-pointer px-4 py-2 border border-[#262626] hover:border-[#DAA520] text-sm transition-colors flex items-center gap-2">
                        {albumCoverUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        {albumCoverUploading ? 'Uploading...' : 'Upload Cover'}
                        <input type="file" accept="image/*" onChange={(e) => handleCoverUpload(e, true)} className="hidden" />
                      </label>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-5">
                    <Switch checked={albumForm.is_downloadable} onCheckedChange={(c) => setAlbumForm(prev => ({ ...prev, is_downloadable: c }))} data-testid="album-downloadable" />
                    <Label>Free Downloads</Label>
                  </div>
                </div>
                </>
                )}

                <div className="border-t border-[#262626] pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-[#DAA520]">{addingToAlbum ? 'New' : ''} Tracks ({albumTracks.length})</Label>
                    <label className="cursor-pointer">
                      <Button type="button" variant="outline" className="rounded-none border-[#262626] hover:border-[#DAA520]" disabled={bulkUploading}
                        onClick={() => fileInputRef.current?.click()} data-testid="bulk-upload-btn">
                        {bulkUploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4 mr-2" /> Select Audio Files</>}
                      </Button>
                      <input ref={fileInputRef} type="file" accept="audio/*" multiple onChange={handleBulkAudioUpload} className="hidden" />
                    </label>
                  </div>

                  {albumTracks.length === 0 ? (
                    <div className="bg-[#0A0A0A] border border-dashed border-[#262626] p-8 text-center">
                      <Music className="w-8 h-8 mx-auto text-[#262626] mb-2" />
                      <p className="text-[#666] text-sm">Select multiple audio files to upload as an album</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-72 overflow-y-auto">
                      {albumTracks.map((track, i) => (
                        <div key={i} className="flex items-center gap-3 bg-[#0A0A0A] border border-[#262626] p-3" data-testid={`album-track-${i}`}>
                          <span className="text-[#666] text-sm font-mono w-6 text-center flex-shrink-0">{i + 1}</span>
                          <GripVertical className="w-4 h-4 text-[#333] flex-shrink-0" />
                          <Input
                            value={track.title}
                            onChange={(e) => updateAlbumTrackTitle(i, e.target.value)}
                            className="bg-transparent border-none h-8 text-sm flex-1 p-0 focus:ring-0"
                            data-testid={`album-track-title-${i}`}
                          />
                          <span className="text-[10px] text-[#666] truncate max-w-[120px] flex-shrink-0">{track.fileName}</span>
                          <button onClick={() => removeAlbumTrack(i)} className="text-[#666] hover:text-[#FF3B30] transition-colors flex-shrink-0" data-testid={`remove-track-${i}`}>
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Button onClick={handleAlbumSubmit} disabled={savingAlbum || albumTracks.length === 0}
                  className="w-full bg-[#DAA520] text-black hover:bg-white rounded-none" data-testid="save-album-btn">
                  {savingAlbum ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : addingToAlbum ? `Add ${albumTracks.length} Track${albumTracks.length !== 1 ? 's' : ''} to Album` : `Save Album (${albumTracks.length} tracks)`}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-none border-[#262626] hover:border-[#DAA520]" data-testid="add-free-media-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Single
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#141414] border-[#262626] rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-cabinet font-bold uppercase">{editing ? 'Edit' : 'Add'} Free Media</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Title *</Label>
                  <Input value={form.title} onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                    required className="bg-[#0A0A0A] border-[#262626] rounded-none" data-testid="free-media-title" />
                </div>
                <div>
                  <Label>Artist</Label>
                  <Input value={form.artist} onChange={(e) => setForm(prev => ({ ...prev, artist: e.target.value }))}
                    className="bg-[#0A0A0A] border-[#262626] rounded-none" data-testid="free-media-artist" />
                </div>
                <div>
                  <Label>Album (optional)</Label>
                  <Input value={form.album_name} onChange={(e) => setForm(prev => ({ ...prev, album_name: e.target.value }))}
                    className="bg-[#0A0A0A] border-[#262626] rounded-none" placeholder="Leave empty for singles" data-testid="free-media-album" />
                </div>
                <div>
                  <Label>Description</Label>
                  <textarea value={form.description} onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full bg-[#0A0A0A] border border-[#262626] p-3 text-white resize-none outline-none" rows={2}
                    data-testid="free-media-description" />
                </div>
                <div>
                  <Label>Release Date <span className="text-[#666] text-xs">(optional)</span></Label>
                  <Input type="date" value={form.release_date || ''} onChange={(e) => setForm(prev => ({ ...prev, release_date: e.target.value }))}
                    className="bg-[#0A0A0A] border-[#262626] rounded-none" data-testid="free-media-release-date" />
                </div>
                <div>
                  <Label>Spotify Link <span className="text-[#666] text-xs">(optional)</span></Label>
                  <Input value={form.spotify_url || ''} onChange={(e) => setForm(prev => ({ ...prev, spotify_url: e.target.value }))}
                    placeholder="https://open.spotify.com/track/..."
                    className="bg-[#0A0A0A] border-[#262626] rounded-none" data-testid="free-media-spotify-url" />
                </div>
                <div>
                  <Label>Cover Image</Label>
                  <div className="flex items-center gap-3 mt-1">
                    {form.cover_image && <img src={form.cover_image} alt="" className="w-14 h-14 object-cover border border-[#262626]" />}
                    <label className="cursor-pointer px-4 py-2 border border-[#262626] hover:border-[#DAA520] text-sm transition-colors flex items-center gap-2">
                      {uploadingCover ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {uploadingCover ? 'Uploading...' : 'Upload Cover'}
                      <input type="file" accept="image/*" onChange={(e) => handleCoverUpload(e)} className="hidden" />
                    </label>
                  </div>
                </div>
                <div>
                  <Label>Back Cover <span className="text-[#666] text-xs">(optional)</span></Label>
                  <div className="flex items-center gap-3 mt-1">
                    {form.back_cover && <img src={form.back_cover} alt="" className="w-14 h-14 object-cover border border-[#262626]" />}
                    <label className="cursor-pointer px-4 py-2 border border-[#262626] hover:border-[#DAA520] text-sm transition-colors flex items-center gap-2" data-testid="upload-back-cover-btn">
                      {uploadingBackCover ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {uploadingBackCover ? 'Uploading...' : 'Upload Back Cover'}
                      <input type="file" accept="image/*" onChange={handleBackCoverUpload} className="hidden" />
                    </label>
                    {form.back_cover && (
                      <button onClick={() => setForm(prev => ({ ...prev, back_cover: '' }))} className="text-red-500 hover:text-red-400" title="Remove back cover">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <Label>Audio File *</Label>
                  <div className="flex items-center gap-3 mt-1">
                    {form.audio_file && <span className="text-xs text-[#22C55E] flex items-center gap-1"><Music className="w-3 h-3" /> Uploaded</span>}
                    <label className="cursor-pointer px-4 py-2 border border-[#262626] hover:border-[#DAA520] text-sm transition-colors flex items-center gap-2">
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {uploading ? 'Uploading...' : 'Upload Audio'}
                      <input type="file" accept="audio/*" onChange={handleAudioUpload} className="hidden" />
                    </label>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={form.is_downloadable} onCheckedChange={(c) => setForm(prev => ({ ...prev, is_downloadable: c }))} data-testid="free-media-downloadable" />
                  <Label>Allow Free Downloads</Label>
                </div>
                <Button type="submit" className="w-full bg-[#DAA520] text-black hover:bg-white rounded-none" data-testid="save-free-media-btn">
                  {editing ? 'Update' : 'Create'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Albums */}
      {albumNames.map((albumName, albumIdx) => {
        const tracks = albums[albumName];
        return (
        <div key={albumName} className="bg-[#141414] border border-[#262626] overflow-hidden" data-testid={`album-group-${albumName}`}>
          <div className="flex items-center gap-4 p-4 border-b border-[#262626] bg-[#0A0A0A]">
            <div className="flex flex-col gap-0.5 flex-shrink-0">
              <button
                onClick={() => moveAlbum(albumNames, albumIdx, -1)}
                disabled={albumIdx === 0 || reordering}
                className={`p-0.5 transition-colors ${albumIdx === 0 ? 'text-[#1A1A1A]' : 'text-[#666] hover:text-[#DAA520]'}`}
                data-testid={`move-album-up-${albumName}`}
              >
                <ArrowUp className="w-3 h-3" />
              </button>
              <button
                onClick={() => moveAlbum(albumNames, albumIdx, 1)}
                disabled={albumIdx === albumNames.length - 1 || reordering}
                className={`p-0.5 transition-colors ${albumIdx === albumNames.length - 1 ? 'text-[#1A1A1A]' : 'text-[#666] hover:text-[#DAA520]'}`}
                data-testid={`move-album-down-${albumName}`}
              >
                <ArrowDown className="w-3 h-3" />
              </button>
            </div>
            <div className="relative group/cover flex-shrink-0">
              {tracks[0]?.cover_image ? (
                <img src={tracks[0].cover_image} alt={albumName} className="w-14 h-14 object-cover border border-[#262626]" />
              ) : (
                <div className="w-14 h-14 bg-[#141414] border border-[#262626] flex items-center justify-center">
                  <Disc className="w-6 h-6 text-[#DAA520]" />
                </div>
              )}
              <label className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover/cover:opacity-100 transition-opacity cursor-pointer" title="Change album cover">
                <Upload className="w-4 h-4 text-white" />
                <input type="file" accept="image/*" onChange={(e) => handleAlbumCoverChange(e, tracks)} className="hidden" />
              </label>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-cabinet font-bold text-lg uppercase truncate">{albumName}</h3>
              <p className="text-[#A3A3A3] text-sm">
                {tracks[0]?.artist || 'Unknown Artist'} &middot; {tracks.length} track{tracks.length > 1 ? 's' : ''}
                {tracks[0]?.release_date && <span className="text-[#DAA520] ml-2">&middot; {tracks[0].release_date}</span>}
              </p>
            </div>
            <label className="cursor-pointer">
              <Button type="button" variant="outline" size="sm" className="rounded-none border-[#262626] hover:border-[#DAA520] pointer-events-none" data-testid={`change-cover-${albumName}`}>
                <Upload className="w-4 h-4 mr-1" /> Cover
              </Button>
              <input type="file" accept="image/*" onChange={(e) => handleAlbumCoverChange(e, tracks)} className="hidden" />
            </label>
            <Button onClick={() => handleAddTracksToAlbum(albumName)} variant="outline" size="sm" className="rounded-none border-[#262626] hover:border-[#DAA520] text-[#DAA520]" data-testid={`add-tracks-${albumName}`}>
              <Plus className="w-4 h-4 mr-1" /> Add Tracks
            </Button>
            <Button onClick={() => handleDeleteAlbum(albumName)} variant="outline" size="sm" className="rounded-none border-[#262626] text-red-500 hover:text-red-400" data-testid={`delete-album-${albumName}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
          <div className="divide-y divide-[#1A1A1A]">
            {tracks.map((item, i) => (
              <div key={item.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-[#1A1A1A] transition-colors" data-testid={`free-media-item-${item.id}`}>
                <div className="flex flex-col gap-0.5 flex-shrink-0">
                  <button
                    onClick={() => moveTrack(tracks, i, -1)}
                    disabled={i === 0 || reordering}
                    className={`p-0.5 transition-colors ${i === 0 ? 'text-[#1A1A1A]' : 'text-[#666] hover:text-[#DAA520]'}`}
                    data-testid={`move-up-${item.id}`}
                  >
                    <ArrowUp className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => moveTrack(tracks, i, 1)}
                    disabled={i === tracks.length - 1 || reordering}
                    className={`p-0.5 transition-colors ${i === tracks.length - 1 ? 'text-[#1A1A1A]' : 'text-[#666] hover:text-[#DAA520]'}`}
                    data-testid={`move-down-${item.id}`}
                  >
                    <ArrowDown className="w-3 h-3" />
                  </button>
                </div>
                <span className="text-[#666] text-xs font-mono w-6 text-center flex-shrink-0">{item.track_number || i + 1}</span>
                <Music className="w-4 h-4 text-[#DAA520] flex-shrink-0" />
                <span className="flex-1 truncate text-sm">{item.title}</span>
                {item.release_date && <span className="text-xs text-[#555] font-mono flex-shrink-0">{item.release_date}</span>}
                <span className="text-xs text-[#666] font-mono"><Play className="w-3 h-3 inline" /> {item.play_count || 0}</span>
                {item.is_downloadable && <span className="text-xs text-[#666] font-mono"><Download className="w-3 h-3 inline" /> {item.download_count || 0}</span>}
                <button
                  onClick={() => toggleDownloadable(item)}
                  className={`flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border transition-colors flex-shrink-0 ${item.is_downloadable ? 'border-[#DAA520] text-[#DAA520] bg-[#DAA520]/10' : 'border-[#333] text-[#666] hover:border-[#DAA520] hover:text-[#DAA520]'}`}
                  title={item.is_downloadable ? 'Click to disable free download' : 'Click to enable free download'}
                  data-testid={`toggle-dl-${item.id}`}
                >
                  <Download className="w-3 h-3" />
                  {item.is_downloadable ? 'FREE' : 'OFF'}
                </button>
                <PreviewModeToggle
                  value={{ preview_mode: item.preview_mode || 'full', unlock_price: item.unlock_price || 0 }}
                  onChange={async (payload) => {
                    await api.put(`${API}/free-media/${item.id}`, payload);
                    onRefresh();
                  }}
                  testid={`fm-${item.id}`}
                />
                <Button onClick={() => openEdit(item)} variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`edit-free-${item.id}`}>
                  <Edit className="w-3 h-3" />
                </Button>
                <Button onClick={() => handleDelete(item.id)} variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" data-testid={`delete-free-${item.id}`}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      );
      })}

      {/* Singles */}
      {singles.length > 0 && (
        <div>
          {Object.keys(albums).length > 0 && <h3 className="font-cabinet font-bold uppercase text-sm text-[#A3A3A3] mb-3">Singles</h3>}
          <div className="space-y-2">
            {singles.map((item, i) => (
              <div key={item.id} className="bg-[#141414] border border-[#262626] p-4 flex items-center gap-4" data-testid={`free-media-item-${item.id}`}>
                <div className="flex flex-col gap-0.5 flex-shrink-0">
                  <button
                    onClick={() => moveTrack(singles, i, -1)}
                    disabled={i === 0 || reordering}
                    className={`p-0.5 transition-colors ${i === 0 ? 'text-[#1A1A1A]' : 'text-[#666] hover:text-[#DAA520]'}`}
                    data-testid={`move-up-${item.id}`}
                  >
                    <ArrowUp className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => moveTrack(singles, i, 1)}
                    disabled={i === singles.length - 1 || reordering}
                    className={`p-0.5 transition-colors ${i === singles.length - 1 ? 'text-[#1A1A1A]' : 'text-[#666] hover:text-[#DAA520]'}`}
                    data-testid={`move-down-${item.id}`}
                  >
                    <ArrowDown className="w-3 h-3" />
                  </button>
                </div>
                {item.cover_image ? (
                  <img src={item.cover_image} alt={item.title} className="w-12 h-12 object-cover border border-[#262626] flex-shrink-0" />
                ) : (
                  <div className="w-12 h-12 bg-[#0A0A0A] border border-[#262626] flex items-center justify-center flex-shrink-0">
                    <Music className="w-5 h-5 text-[#262626]" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold truncate">{item.title}</h4>
                  <div className="flex items-center gap-3 text-xs text-[#666]">
                    {item.artist && <span>{item.artist}</span>}
                    <span><Play className="w-3 h-3 inline" /> {item.play_count || 0}</span>
                    {item.is_downloadable && <span><Download className="w-3 h-3 inline" /> {item.download_count || 0}</span>}
                  </div>
                </div>
                <button
                  onClick={() => toggleDownloadable(item)}
                  className={`flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider border transition-colors flex-shrink-0 ${item.is_downloadable ? 'border-[#DAA520] text-[#DAA520] bg-[#DAA520]/10' : 'border-[#333] text-[#666] hover:border-[#DAA520] hover:text-[#DAA520]'}`}
                  title={item.is_downloadable ? 'Click to disable free download' : 'Click to enable free download'}
                  data-testid={`toggle-dl-${item.id}`}
                >
                  <Download className="w-3 h-3" />
                  {item.is_downloadable ? 'FREE' : 'OFF'}
                </button>
                <Button onClick={() => openEdit(item)} variant="outline" size="sm" className="rounded-none border-[#262626]" data-testid={`edit-free-${item.id}`}>
                  <Edit className="w-4 h-4" />
                </Button>
                <Button onClick={() => handleDelete(item.id)} variant="outline" size="sm" className="rounded-none border-[#262626] text-red-500 hover:text-red-400" data-testid={`delete-free-${item.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {freeMedia.length === 0 && (
        <div className="bg-[#141414] border border-[#262626] p-12 text-center">
          <Music className="w-12 h-12 mx-auto text-[#262626] mb-4" />
          <p className="text-[#A3A3A3]">No free media yet. Upload an album or single!</p>
        </div>
      )}
    </div>
  );
}
